
import React, { useState } from 'react';
import { 
  UserPlus, Mail, X, Save, 
  Key, Check, Settings, 
  LayoutDashboard, Briefcase, Package, 
  Building2, DollarSign, FileText, 
  Activity, PackageSearch, Calendar, Wrench,
  Radio, Target, FolderKanban, FileSignature, TrendingUp,
  BarChart3, Users, Lock, Loader2,
  Wallet, ShieldCheck, CreditCard, Zap
} from 'lucide-react';
import { User, UserRole, Plan, Company } from './types';
import { Card, Badge } from './SharedComponents';

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Master Platform',
  [UserRole.ADMIN]: 'Administrador',
  [UserRole.MANAGER]: 'Gerente',
  [UserRole.STAFF]: 'Operador',
  [UserRole.DRIVER]: 'Motorista',
  [UserRole.CLIENT]: 'Cliente',
  [UserRole.COMERCIAL]: 'Comercial',
  [UserRole.LOGISTICA]: 'Logística',
  [UserRole.MONTAGEM]: 'Montagem',
  [UserRole.FINANCEIRO]: 'Financeiro',
  [UserRole.OPERACIONAL]: 'Operacional'
};

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'Acesso total à infraestrutura master e todas as locadoras.',
  [UserRole.ADMIN]: 'Gestão completa da unidade, incluindo faturamento e usuários.',
  [UserRole.MANAGER]: 'Gerenciamento de equipes, estoque e operações diárias.',
  [UserRole.STAFF]: 'Acesso operacional para execução de tarefas e ordens.',
  [UserRole.DRIVER]: 'Visão simplificada para motoristas e entregadores.',
  [UserRole.CLIENT]: 'Portal do cliente para acompanhamento de pedidos e faturas.',
  [UserRole.COMERCIAL]: 'Foco em leads, orçamentos e conversão de vendas.',
  [UserRole.LOGISTICA]: 'Gestão de estoque, separação e rotas de entrega.',
  [UserRole.MONTAGEM]: 'Interface para equipes de campo e montagem técnica.',
  [UserRole.FINANCEIRO]: 'Controle de fluxo de caixa, pagamentos e notas fiscais.',
  [UserRole.OPERACIONAL]: 'Execução de processos internos e suporte logístico.'
};

const ROLE_DEFAULT_MODULES: Record<UserRole, string[]> = {
  [UserRole.SUPER_ADMIN]: ['dashboard', 'monitor', 'companies', 'global-finance', 'core-api', 'settings'],
  [UserRole.ADMIN]: ['dashboard', 'monitor', 'leads', 'estimates', 'contracts', 'projects', 'goals', 'calendar', 'orders', 'separation', 'products', 'teams', 'maintenance', 'quality', 'event-finance', 'event-profitability', 'invoices', 'payments', 'internal-finance', 'clients', 'users', 'settings'],
  [UserRole.MANAGER]: ['dashboard', 'monitor', 'calendar', 'orders', 'separation', 'products', 'teams', 'maintenance', 'quality', 'clients'],
  [UserRole.STAFF]: ['dashboard', 'calendar', 'orders', 'separation', 'products'],
  [UserRole.DRIVER]: ['dashboard', 'orders', 'calendar'],
  [UserRole.CLIENT]: ['dashboard', 'orders', 'invoices'],
  [UserRole.COMERCIAL]: ['dashboard', 'leads', 'estimates', 'contracts', 'projects', 'goals', 'clients'],
  [UserRole.LOGISTICA]: ['dashboard', 'calendar', 'orders', 'separation', 'products', 'teams', 'maintenance', 'quality'],
  [UserRole.MONTAGEM]: ['dashboard', 'orders', 'calendar', 'maintenance'],
  [UserRole.FINANCEIRO]: ['dashboard', 'event-finance', 'event-profitability', 'invoices', 'payments', 'internal-finance'],
  [UserRole.OPERACIONAL]: ['dashboard', 'orders', 'separation', 'products', 'calendar']
};

interface UserManagementPageProps {
  users: User[];
  plans: Plan[];
  onAddUser: (staffData: any) => Promise<void>;
  onUpdateUser: (userId: string, updates: any) => Promise<void>;
  addNotification: (title: string, message: string, type: 'info' | 'warning' | 'error' | 'success') => void;
  isMasterView?: boolean;
  user?: User;
  currentCompany?: Company;
}

const UNIT_MODULES = [
  { id: 'dashboard', label: 'Painel Geral', group: 'Dashboard', icon: LayoutDashboard },
  { id: 'monitor', label: 'Radar Live GPRS', group: 'Dashboard', icon: Radio },
  
  { id: 'leads', label: 'Leads CRM', group: 'Comercial', icon: Target },
  { id: 'estimates', label: 'Orçamentos', group: 'Comercial', icon: Wallet },
  { id: 'contracts', label: 'Contratos', group: 'Comercial', icon: FileSignature },
  { id: 'projects', label: 'Projetos', group: 'Comercial', icon: FolderKanban },
  { id: 'goals', label: 'Metas Vendas', group: 'Comercial', icon: TrendingUp },
  
  { id: 'calendar', label: 'Agenda', group: 'Logística', icon: Calendar },
  { id: 'orders', label: 'Ordens O.S.', group: 'Logística', icon: Briefcase },
  { id: 'separation', label: 'Expedição', group: 'Logística', icon: Package },
  { id: 'products', label: 'Inventário', group: 'Logística', icon: PackageSearch },
  { id: 'teams', label: 'Equipes', group: 'Logística', icon: Users },
  { id: 'maintenance', label: 'Oficina', group: 'Logística', icon: Wrench },
  { id: 'quality', label: 'IA Avarias', group: 'Logística', icon: ShieldCheck },
  
  { id: 'event-finance', label: 'Controladoria', group: 'Financeiro', icon: ShieldCheck },
  { id: 'event-profitability', label: 'Lucro Evento', group: 'Financeiro', icon: BarChart3 },
  { id: 'invoices', label: 'Faturas / NF', group: 'Financeiro', icon: FileText },
  { id: 'payments', label: 'Pagamentos', icon: CreditCard, group: 'Financeiro' },
  { id: 'internal-finance', label: 'Fluxo Caixa', group: 'Financeiro', icon: Activity },
  
  { id: 'clients', label: 'Parceiros', group: 'Sistema', icon: Building2 },
  { id: 'users', label: 'Acessos', group: 'Sistema', icon: Key },
  { id: 'settings', label: 'Configurações', group: 'Sistema', icon: Settings },
];

const MASTER_PLATFORM_MODULES = [
  { id: 'companies', label: 'Locadoras SaaS', group: 'Infraestrutura Master', icon: Building2 },
  { id: 'global-finance', label: 'Dashboard MRR', group: 'Infraestrutura Master', icon: DollarSign },
  { id: 'core-api', label: 'Core Engine (IA/API)', group: 'Infraestrutura Master', icon: Zap },
];

export const UserManagementPage = ({ users, plans, onAddUser, onUpdateUser, addNotification, isMasterView = false, user, currentCompany }: UserManagementPageProps) => {
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    role: isMasterView ? UserRole.SUPER_ADMIN : UserRole.ADMIN,
    authorizedTabs: [] as string[]
  });

  const availableTabs = isMasterView 
    ? [...MASTER_PLATFORM_MODULES, ...UNIT_MODULES] 
    : UNIT_MODULES;

  const groups = Array.from(new Set(availableTabs.map(t => t.group)));

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (editingUser) {
        await onUpdateUser(editingUser.id, formData);
        addNotification('Sucesso', 'Acesso atualizado com sucesso.', 'success');
        closeModal();
      } else {
        // CRIAÇÃO REAL NO BANCO + DISPARO DE E-MAIL
        await onAddUser(formData);
        addNotification('Sucesso', 'Novo login gerado e onboarding enviado por e-mail.', 'success');
        closeModal();
      }
    } catch (err: any) {
      addNotification('Erro', `Falha ao processar: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setFormData({ 
        name: '', 
        email: '', 
        password: '', 
        role: isMasterView ? UserRole.SUPER_ADMIN : UserRole.ADMIN, 
        authorizedTabs: [] 
    });
  };

  const toggleTab = (tabId: string) => {
    setFormData(prev => ({
      ...prev,
      authorizedTabs: prev.authorizedTabs.includes(tabId)
        ? prev.authorizedTabs.filter(id => id !== tabId)
        : [...prev.authorizedTabs, tabId]
    }));
  };

  const handleRoleChange = (role: UserRole) => {
    setFormData(prev => ({
      ...prev,
      role,
      authorizedTabs: ROLE_DEFAULT_MODULES[role] || []
    }));
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 p-4 md:p-8 lg:p-10">
      <div className="flex justify-between items-end px-2">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tighter leading-none text-secondary">
            {isMasterView ? 'Gestão de Operadores Master' : 'Acessos & Privilégios'}
          </h1>
          <p className="text-gray-400 text-sm font-medium mt-2 italic">Gerencie quem pode acessar esta unidade.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="bg-primary text-white px-8 py-4 rounded-[20px] font-black uppercase text-xs tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 transition-all flex items-center justify-center gap-3"
        >
          <UserPlus size={18} /> Novo Acesso
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {users.filter(u => u.name.toLowerCase().includes(search.toLowerCase())).map((u: User) => (
          <Card key={u.id} className="p-8 border-2 group transition-all relative overflow-hidden flex flex-col justify-between h-full bg-white border-gray-100 hover:border-primary/20">
            <div>
              <div className="flex justify-between items-start mb-6">
                 <div className={`w-16 h-16 rounded-[24px] flex items-center justify-center font-black text-2xl overflow-hidden border-4 shadow-xl ${isMasterView ? 'bg-primary text-white border-white' : 'bg-secondary text-white border-white'}`}>
                    {u.avatar ? <img src={u.avatar} className="w-full h-full object-cover" /> : u.name.charAt(0)}
                 </div>
                 <Badge className="bg-secondary/5 text-secondary border-none font-black text-[9px] py-1 px-3 uppercase">{(ROLE_LABELS as any)[u.role]}</Badge>
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight text-secondary">{u.name}</h3>
              <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase flex items-center gap-2"><Mail size={12} /> {u.email}</p>
            </div>
            <button 
                onClick={() => { setEditingUser(u); setFormData({name: u.name, email: u.email, password: '', role: u.role, authorizedTabs: u.authorizedTabs || []}); setIsModalOpen(true); }}
                className="mt-8 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest bg-gray-50 text-gray-400 hover:bg-secondary hover:text-white flex items-center justify-center gap-2 transition-all"
            >
                <Settings size={14} /> Configurar
            </button>
          </Card>
        ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-secondary/95 backdrop-blur-xl z-[500] flex items-center justify-center p-4">
           <div className="w-full max-w-4xl bg-white rounded-[48px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-8 bg-[#1e293b] text-white flex justify-between items-center shrink-0">
                 <div className="flex items-center gap-5">
                    <div className="p-4 bg-primary text-white rounded-2xl shadow-lg"><UserPlus size={32} /></div>
                    <div>
                        <h2 className="text-2xl font-black uppercase tracking-tight italic">{editingUser ? 'Editar Acesso' : 'Gerar Novo Login'}</h2>
                        <p className="text-[10px] font-bold text-primary uppercase tracking-widest mt-1">Onboarding automático via e-mail</p>
                    </div>
                 </div>
                 <button onClick={closeModal} className="p-3 bg-white/10 hover:bg-red-500/20 rounded-2xl"><X size={32} /></button>
              </div>

              <form onSubmit={handleSaveUser} className="p-10 space-y-10 overflow-y-auto scrollbar-hide flex-1">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="space-y-6">
                       <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b pb-3 italic border-primary/20">Identificação</h3>
                       <div className="space-y-4">
                          <InputGroup label="Nome Completo" value={formData.name} onChange={(v:any) => setFormData({...formData, name: v})} required />
                          <InputGroup label="E-mail de Acesso" value={formData.email} onChange={(v:any) => setFormData({...formData, email: v})} required type="email" />
                          <div className="space-y-1.5">
                             <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Senha Temporária</label>
                             <div className="relative">
                                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary opacity-30" />
                                <input required className="w-full p-4 pl-12 rounded-2xl bg-gray-50 border-2 border-gray-100 font-bold outline-none focus:border-primary transition-all" type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                             </div>
                          </div>
                          <div className="space-y-1.5">
                             <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Nível de Permissão</label>
                             <select 
                               className="w-full p-4 rounded-2xl bg-gray-50 border-2 border-gray-100 font-bold outline-none cursor-pointer focus:border-primary transition-all" 
                               value={formData.role} 
                               onChange={e => handleRoleChange(e.target.value as any)}
                             >
                                {Object.values(UserRole).filter(r => isMasterView ? r === UserRole.SUPER_ADMIN : r !== UserRole.SUPER_ADMIN).map(r => (
                                    <option key={r} value={r}>{ROLE_LABELS[r as keyof typeof ROLE_LABELS]}</option>
                                ))}
                             </select>
                             <p className="text-[9px] font-medium text-gray-400 mt-2 px-1 italic">
                               {ROLE_DESCRIPTIONS[formData.role]}
                             </p>
                          </div>
                       </div>
                    </div>

                    <div className="space-y-6">
                       <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] border-b pb-3 italic border-primary/20">Módulos do Painel</h3>
                       <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2 scrollbar-hide">
                          {groups.map(group => (
                            <div key={group} className="space-y-3">
                               <div className="flex items-center justify-between">
                                  <h4 className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{group}</h4>
                                  <button 
                                    type="button"
                                    onClick={() => {
                                      const groupTabs = availableTabs.filter(t => t.group === group).map(t => t.id);
                                      const allSelected = groupTabs.every(id => formData.authorizedTabs.includes(id));
                                      
                                      setFormData(prev => ({
                                        ...prev,
                                        authorizedTabs: allSelected
                                          ? prev.authorizedTabs.filter(id => !groupTabs.includes(id))
                                          : Array.from(new Set([...prev.authorizedTabs, ...groupTabs]))
                                      }));
                                    }}
                                    className="text-[8px] font-black text-primary uppercase tracking-tighter hover:underline"
                                  >
                                    {availableTabs.filter(t => t.group === group).every(id => formData.authorizedTabs.includes(id.id)) ? 'Desmarcar Tudo' : 'Selecionar Tudo'}
                                  </button>
                               </div>
                               <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {availableTabs.filter(t => t.group === group).map(tab => (
                                    <button 
                                      key={tab.id}
                                      type="button"
                                      onClick={() => toggleTab(tab.id)}
                                      className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                                        formData.authorizedTabs.includes(tab.id) 
                                          ? 'bg-primary border-primary text-white shadow-lg shadow-primary/10' 
                                          : 'bg-white border-gray-100 text-gray-400 hover:border-primary/20'
                                      }`}
                                    >
                                       <div className="flex items-center gap-2">
                                          <tab.icon size={14} />
                                          <span className="text-[9px] font-black uppercase tracking-tight">{tab.label}</span>
                                       </div>
                                       {formData.authorizedTabs.includes(tab.id) && <Check size={12} strokeWidth={4} />}
                                    </button>
                                  ))}
                               </div>
                            </div>
                          ))}
                       </div>
                    </div>
                 </div>

                 <div className="pt-10 border-t border-gray-100 flex gap-4">
                    <button type="button" onClick={closeModal} className="flex-1 py-5 bg-gray-50 text-gray-400 rounded-[24px] font-black uppercase text-[10px] tracking-widest hover:bg-gray-100">Cancelar</button>
                    <button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="flex-[2] py-5 bg-primary text-white rounded-[24px] font-black uppercase text-xs tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-3"
                    >
                       {/* Fix: Added Loader2 to imports from lucide-react */}
                       {isSubmitting ? <Loader2 size={22} className="animate-spin" /> : <Save size={22} />}
                       {isSubmitting ? 'Gerando Acesso...' : 'Efetivar e Enviar Onboarding'}
                    </button>
                 </div>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

const InputGroup = ({ label, value, onChange, type = "text", required }: any) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">{label}</label>
    <input required={required} type={type} className="w-full p-4 rounded-2xl bg-gray-50 border-2 border-gray-100 font-bold outline-none focus:border-primary transition-all text-sm" value={value} onChange={e => onChange(e.target.value)} />
  </div>
);
